import type { MouseEvent } from 'react';

/**
 * 🔴 THE TILL'S NUMBER PAD — design-system plan DS-8 (5.1; F574, F224, F210). A counter's touch screen has no
 * keyboard worth typing a price on: the phone keyboard covers the basket and puts the digits behind a shift. Peer POS
 * products use a pad of big keys, and so does this: 7 8 9 / 4 5 6 / 1 2 3 / 00 0 . with ⌫ and Enter down the right,
 * each key at least 56px.
 *
 * Controlled: it is handed the field's text and gives back the next text (`pressKey`), so the field stays the one
 * place the value lives and a person may still type into it. A key press does not take focus from the field (the
 * caret stays where the person is looking). Every key is a named button: "Decimal point", "Delete last digit".
 * `decimal={false}` disables the point, for a whole number; `onEnter` adds the Enter key (a `PromptDialog` wires it to
 * its confirm).
 */
export type KeypadKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '00' | '.' | 'back';

const DIGITS: KeypadKey[] = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '00', '0', '.'];

/** The text after one key: a digit or 00 is added at the end, the point once, ⌫ takes the last character away. */
export function pressKey(value: string, key: KeypadKey, decimal = true): string {
  if (key === 'back') return value.slice(0, -1);
  if (key === '.') {
    if (!decimal || value.includes('.')) return value;
    return value === '' ? '0.' : `${value}.`;
  }
  // A leading zero is replaced, not kept: "0" then "5" is 5, and "00" on nothing is a single 0.
  if (value === '' || value === '0') return key === '00' ? '0' : key;
  return `${value}${key}`;
}

/** Keep focus in the field the pad types into. */
const keepFocus = (e: MouseEvent) => e.preventDefault();

export function Keypad({
  value,
  onChange,
  decimal = true,
  onEnter,
  enterLabel = 'Enter',
  label = 'Keypad',
}: {
  value: string;
  onChange: (next: string) => void;
  decimal?: boolean;
  onEnter?: () => void;
  enterLabel?: string;
  /** The group's name, read before its keys. */
  label?: string;
}) {
  return (
    <div className={onEnter ? 'wb-keypad' : 'wb-keypad wb-keypad--no-enter'} role="group" aria-label={label}>
      {DIGITS.map((key) => (
        <button
          key={key}
          type="button"
          className="wb-keypad__key"
          aria-label={key === '.' ? 'Decimal point' : undefined}
          disabled={key === '.' && !decimal}
          onMouseDown={keepFocus}
          onClick={() => onChange(pressKey(value, key, decimal))}
        >
          {key}
        </button>
      ))}
      <button
        type="button"
        className="wb-keypad__key wb-keypad__key--back"
        aria-label="Delete last digit"
        onMouseDown={keepFocus}
        onClick={() => onChange(pressKey(value, 'back', decimal))}
      >
        ⌫
      </button>
      {onEnter ? (
        <button type="button" className="wb-keypad__key wb-keypad__key--enter" onMouseDown={keepFocus} onClick={onEnter}>
          {enterLabel}
        </button>
      ) : null}
    </div>
  );
}
